import { Suspense, lazy, Component } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import Layout from './components/layout/Layout'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { CartProvider } from './contexts/CartContext'
import { AdminToastProvider } from './contexts/AdminToastContext'

class ErrorBoundary extends Component {
  state = { error: null }
  static getDerivedStateFromError(error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-brand-bg px-4">
          <div className="text-center">
            <p className="font-heading text-4xl text-brand-red mb-4">Oops!</p>
            <p className="text-gray-600 mb-6">Something went wrong loading this page.</p>
            <a href="/" className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 rounded-md font-heading font-bold uppercase tracking-wide hover:bg-[#c1121f] transition-colors">
              Back to Home
            </a>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Lazy-load pages for code splitting
const Home = lazy(() => import('./pages/Home'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const Reviews = lazy(() => import('./pages/Reviews'))
const About = lazy(() => import('./pages/About'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const Signup = lazy(() => import('./pages/Signup'))
const TireViewer = lazy(() => import('./pages/TireViewer'))
const Cart = lazy(() => import('./pages/Cart'))
const Checkout = lazy(() => import('./pages/Checkout'))
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'))
const Orders = lazy(() => import('./pages/Orders'))
const OrderDetail = lazy(() => import('./pages/OrderDetail'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Addresses = lazy(() => import('./pages/Addresses'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'))
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'))
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'))
const AdminCustomerDetail = lazy(() => import('./pages/admin/AdminCustomerDetail'))
const AdminShops = lazy(() => import('./pages/admin/AdminShops'))
const AdminShopDetail = lazy(() => import('./pages/admin/AdminShopDetail'))

// Loading fallback
function PageLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-brand-bg"
      role="status"
      aria-live="polite"
      aria-label="Loading page"
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className="w-10 h-10 border-4 border-brand-silver border-t-brand-red rounded-full animate-spin"
          aria-hidden="true"
        />
        <p className="text-brand-silver text-sm font-medium">Loading&hellip;</p>
      </div>
    </div>
  )
}

// Protected route — redirects to /login if not authenticated
function RequireAuth({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <PageLoader />
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />
  return children
}

// Admin-only route — requires fasttrackadmin or admin role
function RequireAdmin({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <PageLoader />
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />
  const isAdmin = user.roles?.includes('fasttrackadmin') || user.roles?.includes('admin') || user.roles?.includes('datium')
  if (!isAdmin) return <Navigate to="/" replace />
  return children
}

// Public-only route — redirects away if already logged in
function PublicOnly({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  if (user) return <Navigate to="/" replace />
  return children
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Layout><Home /></Layout>} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/reviews" element={<Reviews />} />
                  <Route path="/about" element={<About />} />

                  <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                  <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                  <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />

                  <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                  <Route path="/tire-viewer" element={<TireViewer />} />
                  <Route path="/cart" element={<RequireAuth><Layout><Cart /></Layout></RequireAuth>} />
                  <Route path="/checkout" element={<RequireAuth><Layout><Checkout /></Layout></RequireAuth>} />
                  <Route path="/order-confirmation/:orderId" element={<RequireAuth><Layout><OrderConfirmation /></Layout></RequireAuth>} />
                  <Route path="/orders" element={<RequireAuth><Layout><Orders /></Layout></RequireAuth>} />
                  <Route path="/orders/:orderId" element={<RequireAuth><Layout><OrderDetail /></Layout></RequireAuth>} />
                  <Route path="/account/addresses" element={<RequireAuth><Layout><Addresses /></Layout></RequireAuth>} />
                  <Route path="/account/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
                  <Route path="/admin" element={<RequireAdmin><AdminToastProvider><AdminDashboard /></AdminToastProvider></RequireAdmin>} />
                  <Route path="/admin/orders" element={<RequireAdmin><AdminToastProvider><AdminOrders /></AdminToastProvider></RequireAdmin>} />
                  <Route path="/admin/orders/:orderId" element={<RequireAdmin><AdminToastProvider><AdminOrderDetail /></AdminToastProvider></RequireAdmin>} />
                  <Route path="/admin/customers" element={<RequireAdmin><AdminToastProvider><AdminCustomers /></AdminToastProvider></RequireAdmin>} />
                  <Route path="/admin/customers/:userId" element={<RequireAdmin><AdminToastProvider><AdminCustomerDetail /></AdminToastProvider></RequireAdmin>} />
                  <Route path="/admin/shops" element={<RequireAdmin><AdminToastProvider><AdminShops /></AdminToastProvider></RequireAdmin>} />
                  <Route path="/admin/shops/:shopId" element={<RequireAdmin><AdminToastProvider><AdminShopDetail /></AdminToastProvider></RequireAdmin>} />

                  {/* 404 fallback */}
                  <Route
                    path="*"
                    element={
                      <Layout>
                        <div className="min-h-screen flex items-center justify-center bg-brand-bg">
                          <div className="text-center px-4">
                            <p className="font-heading text-7xl text-brand-red mb-4" aria-label="404 error">404</p>
                            <h1 className="font-heading text-2xl text-brand-charcoal mb-4">Page Not Found</h1>
                            <p className="text-gray-600 mb-8 max-w-sm mx-auto">
                              The page you&apos;re looking for doesn&apos;t exist. Let us get you back on track.
                            </p>
                            <a
                              href="/"
                              className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3.5 rounded-md font-heading font-bold uppercase tracking-wide hover:bg-[#c1121f] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red min-h-[44px]"
                            >
                              Go to Homepage
                            </a>
                          </div>
                        </div>
                      </Layout>
                    }
                  />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  )
}
